/**
 * Lightweight logging service.
 *
 * - logger.debug(...)  → dev only, suppressed in production
 * - logger.log(...)    → dev only, suppressed in production
 * - logger.warn(...)   → dev only, suppressed in production
 * - logger.error(...)  → ALWAYS active (errors are critical)
 *
 * All methods sanitize objects to redact sensitive fields before output.
 */

const IS_DEV = process.env.REACT_APP_VERIFY === "dev";

const SENSITIVE_KEYS = new Set([
    "password",
    "newpassword",
    "confirmpassword",
    "apikey",
    "api_key",
    "token",
    "accesstoken",
    "access_token",
    "refreshtoken",
    "refresh_token",
    "jwt",
    "authorization",
    "secret",
    "passphrase",
    "creditcard",
    "credit_card",
    "cardnumber",
    "card_number",
    "cvv",
    "ssn",
    "securitykey",
    "initvector",
    "userpassword",
]);

function isSensitiveKey(key: string): boolean {
    return SENSITIVE_KEYS.has(key.toLowerCase().replace(/[-_]/g, ""));
}

/**
 * Shallow-sanitizes an object (top-level + one nested level).
 * Replaces sensitive field values with "[REDACTED]".
 */
function sanitize(obj: unknown): unknown {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== "object") return obj;

    if (obj instanceof Error) {
        return { name: obj.name, message: obj.message, stack: obj.stack };
    }

    if (Array.isArray(obj)) {
        return obj.map((item) => sanitize(item));
    }

    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (isSensitiveKey(key)) {
            sanitized[key] = "[REDACTED]";
        } else if (
            typeof value === "object" &&
            value !== null &&
            !(value instanceof Error) &&
            !Array.isArray(value)
        ) {
            const nested: Record<string, unknown> = {};
            for (const [nk, nv] of Object.entries(
                value as Record<string, unknown>
            )) {
                nested[nk] = isSensitiveKey(nk) ? "[REDACTED]" : nv;
            }
            sanitized[key] = nested;
        } else {
            sanitized[key] = value;
        }
    }
    return sanitized;
}

function sanitizeArgs(args: unknown[]): unknown[] {
    return args.map((arg) =>
        typeof arg === "object" && arg !== null ? sanitize(arg) : arg
    );
}

export const logger = {
    debug(...args: unknown[]): void {
        if (!IS_DEV) return;
        // eslint-disable-next-line no-console
        console.debug(...sanitizeArgs(args));
    },

    log(...args: unknown[]): void {
        if (!IS_DEV) return;
        // eslint-disable-next-line no-console
        console.log(...sanitizeArgs(args));
    },

    warn(...args: unknown[]): void {
        if (!IS_DEV) return;
        // eslint-disable-next-line no-console
        console.warn(...sanitizeArgs(args));
    },

    error(...args: unknown[]): void {
        // eslint-disable-next-line no-console
        console.error(...sanitizeArgs(args));
    },
};
